import { ProgressBar } from 'react-bootstrap'
import { MdArrowDropUp } from 'react-icons/md'
import moment from 'moment-timezone'

import './Goals.css'
import { getDailyTargetGuidance, getGoalTargetLabel } from './goalUtils'
import { formatTargetPhasePosition } from './goalPhases'

const cycleBoundary = (value, timezone, end = false) => {
    if (!value) return null

    // Date-only cycle boundaries use the same 02:00 operational-day cutoff as campaigns.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const boundary = moment.tz(`${value} 02:00`, 'YYYY-MM-DD HH:mm', timezone)
        return end ? boundary.add(1, 'day') : boundary
    }

    return moment(value)
}

export const getExpectedProgress = (period, timezone) => {
    const start = cycleBoundary(period?.periodStart, timezone)
    const end = cycleBoundary(period?.periodEnd, timezone, true)
    if (!start?.isValid() || !end?.isValid() || !end.isAfter(start)) return null

    const now = moment.tz(timezone)
    const endOfToday = now.clone().startOf('day').hour(2)
    if (!endOfToday.isAfter(now)) endOfToday.add(1, 'day')

    const projectionTime = Math.min(end.valueOf(), endOfToday.valueOf())
    const elapsed = projectionTime - start.valueOf()
    return Math.min(100, Math.max(0, (elapsed / (end.valueOf() - start.valueOf())) * 100))
}

const getEvaluationTime = (period, timezone) => {
    const end = cycleBoundary(period?.periodEnd, timezone, true)
    if (!end?.isValid()) return null

    return {
        relative: end.from(moment.tz(timezone)),
        exact: end.format('D MMM YYYY, HH:mm'),
    }
}

const getProjectedTarget = (target, expectedPercentage) => {
    const threshold = Number(target.threshold)
    if (!Number.isFinite(threshold) || !Number.isFinite(expectedPercentage)) return null

    return threshold * expectedPercentage / 100
}

export const getRemainingOperationalDays = (period, timezone) => {
    const start = cycleBoundary(period?.periodStart, timezone)
    const end = cycleBoundary(period?.periodEnd, timezone, true)
    if (!start?.isValid() || !end?.isValid() || !end.isAfter(start)) return null

    const now = moment.tz(timezone)
    const endOfToday = now.clone().startOf('day').hour(2)
    if (!endOfToday.isAfter(now)) endOfToday.add(1, 'day')

    // Include the current operational day: at any point on day 3 of a seven-day
    // period, days 3–7 are the five daily opportunities still available.
    const startOfToday = endOfToday.clone().subtract(1, 'day')
    const remainingStart = moment.max(start, startOfToday)
    if (!end.isAfter(remainingStart)) return null

    return Math.max(1, Math.ceil(end.diff(remainingStart, 'days', true)))
}

const formatTargetValue = (value) => value.toLocaleString(undefined, {
    maximumFractionDigits: 2,
})

export const CurrentPeriodHeading = ({ period, timezone }) => {
    const evaluation = getEvaluationTime(period, timezone)

    return (
        <div className="goal-period-heading">
            <div className="goal-current-period-label">
                <strong>Current period{period.targetPhase ? ` · ${formatTargetPhasePosition(period.targetPhase)}` : ''}</strong>
                {evaluation && (
                    <span title={`Evaluation on ${evaluation.exact}`}>
                        Evaluation {evaluation.relative}
                    </span>
                )}
            </div>
            <span>{moment(period.periodStart).format('D MMM')} – {moment(period.periodEnd).format('D MMM YYYY')}</span>
        </div>
    )
}

export const TargetProgress = ({ target, questions, expectedPercentage = null, remainingDays = null, compact = false }) => {
    const projectedTarget = getProjectedTarget(target, expectedPercentage)
    const dailyGuidance = getDailyTargetGuidance(target, remainingDays)

    return (
        <div className={`goal-target-progress${compact ? ' goal-target-progress-compact' : ''}`}>
            <div className="goal-target-progress-label">
                <span>{getGoalTargetLabel(target, questions)}</span>
                <strong>{Number(target.actual || 0).toFixed(target.metric === 'durationMinutes' || target.metric === 'numericSum' ? 1 : 0)} / {target.threshold}</strong>
            </div>
            <div className="goal-progress-track">
                <ProgressBar now={target.percentage || 0} variant={target.achieved ? 'success' : 'info'} />
                {Number.isFinite(expectedPercentage) && (
                    <span
                        className="goal-expected-progress"
                        style={{ '--goal-expected-progress': `${expectedPercentage}%` }}
                        title={`Target by end of today: ${Math.round(expectedPercentage)}%`}
                        aria-label={`Target by end of today: ${Math.round(expectedPercentage)}%`}
                    >
                        <MdArrowDropUp aria-hidden="true" />
                    </span>
                )}
            </div>
            {projectedTarget !== null && (
                <div className="goal-projected-target-label">
                    <span>Target by end of today</span>
                    <strong>{formatTargetValue(projectedTarget)}</strong>
                </div>
            )}
            {dailyGuidance && (
                <div className="goal-daily-targets">
                    <div
                        className="goal-daily-target"
                        title={`Today's required pace was fixed at 02:00 from the progress recorded through yesterday (${dailyGuidance.remainingDays} days remained)`}
                    >
                        <div className="goal-daily-target-value">
                            <strong>{formatTargetValue(dailyGuidance.remainingToday)}</strong>
                            <span>/ {formatTargetValue(dailyGuidance.requiredDailyPace)}</span>
                        </div>
                        <div className="goal-daily-target-labels">
                            <span>remaining today</span>
                            <span>required today</span>
                        </div>
                        <div className="goal-daily-completed">
                            <strong>{formatTargetValue(dailyGuidance.todayActual)}</strong> completed today
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
